<script setup lang="ts">
/**
 * Bloc factuel citable (GEO) des pages charte « apple » (Karim 2026-10-18) :
 * chapitre gris, grand titre + chapô (slot `intro`, texte riche) et lien
 * d'inscription, puis des cartes blanches très arrondies — photo en haut,
 * titre fort, texte COMPLET (c'est lui que citent Google et les IA), lien
 * « Découvrir › ». Zoom lent de la photo et soulèvement doux au survol.
 */
import type { RouteLocationRaw } from "vue-router";
import { TITRE_CARTE_APPLE, TITRE_SECTION_APPLE } from "~/utils/titresApple";

export interface CarteFactuelle {
  titre: string;
  texte: string;
  image: string;
  alt: string;
  href: RouteLocationRaw;
}

const props = defineProps<{
  titre: string;
  cartes: CarteFactuelle[];
  /** Libellé du lien de chaque carte (« Découvrir »). */
  lienCarte: string;
  ctaTexte?: string;
  ctaSource?: string;
}>();

const registerLink = useRegisterLink();
// Espace insécable avant « : ; ? ! » : jamais de ponctuation seule en début de ligne.
const titreAffiche = computed(() => props.titre.replace(/ ([:;?!])/g, " $1"));
</script>

<template>
  <section class="bg-[#f5f5f7] px-4 py-20 sm:px-8 lg:py-28">
    <div class="mx-auto max-w-[1120px]">
      <div class="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div class="max-w-[760px]">
          <h2 class="pa-reveal mb-5 text-[32px] text-[#1d1d1f] [text-wrap:balance] sm:text-[44px]" :style="TITRE_SECTION_APPLE">
            {{ titreAffiche }}
          </h2>
          <div v-if="$slots.intro" class="pa-reveal text-[17px] font-medium leading-[1.55] text-[#424245]">
            <slot name="intro" />
          </div>
        </div>
        <div v-if="props.ctaTexte" class="pa-reveal shrink-0">
          <LienApple v-register-cta="props.ctaSource" :to="registerLink">{{ props.ctaTexte }}</LienApple>
        </div>
      </div>

      <div class="grid gap-5 sm:grid-cols-2" :class="props.cartes.length >= 4 && 'lg:grid-cols-4'">
        <NuxtLink
          v-for="(carte, i) in props.cartes"
          :key="carte.titre"
          :to="carte.href"
          class="carte pa-reveal group flex flex-col overflow-hidden rounded-[28px] bg-white"
          :style="{ '--d': `${(i % 4) * 80}ms` }"
        >
          <div class="relative mx-3 mt-3 aspect-[4/3] overflow-hidden rounded-[20px] bg-[#e8e8ed]">
            <NuxtImg
              :src="carte.image"
              :alt="carte.alt"
              class="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
              sizes="xs:92vw sm:50vw lg:280px"
            />
          </div>
          <div class="flex flex-1 flex-col p-6 pt-5">
            <h3 class="mb-2 text-[19px] text-[#1d1d1f]" :style="TITRE_CARTE_APPLE">{{ carte.titre }}</h3>
            <p class="mb-5 text-[14px] leading-[1.55] text-[#424245]">{{ carte.texte }}</p>
            <span class="carte-lien mt-auto inline-flex items-center gap-1 text-[14px] font-semibold text-(--color-main)">
              {{ props.lienCarte }}
              <UIcon name="i-lucide-chevron-right" class="carte-chevron size-4" aria-hidden="true" />
            </span>
          </div>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<style scoped>
.carte {
  transition:
    transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    box-shadow 0.4s ease;
}
.carte-chevron {
  transition: transform 0.35s cubic-bezier(0.28, 0, 0.22, 1);
}
@media (hover: hover) {
  .carte:hover {
    transform: translateY(-3px);
    box-shadow: 0 24px 50px -30px rgba(0, 0, 0, 0.3);
  }
  .carte:hover .carte-chevron {
    transform: translateX(3px);
  }
}
</style>
